import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../lib/api";
import { newRefundIntentId } from "../../../lib/refundIntentId";
import { lineTaxableMap, taxMarkedName } from "../../../lib/retail/lineTax";
import { toast } from "sonner";
import { Loader2, X, RefreshCw, Banknote, CreditCard, AlertTriangle } from "lucide-react";

// Sep 2026 — moved to `lib/refundIntentId.js` so the duplicate-collection
// refund control shares exactly this identity scheme. See that module.

// A request whose outcome is genuinely UNKNOWN: it never reached a
// verdict (network failure, timeout, aborted) or the server could not
// answer (5xx / 408 / 429). In all of these the refund may already have
// been accepted by the provider, so the intent id must be RETAINED.
function isAmbiguousRefundError(e) {
  const status = e?.response?.status;
  if (status == null) return true;              // no response at all
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  return false;
}

// A verdict the server actually reached and that moved no money — safe
// to abandon this intent id and mint a new one for the next attempt.
function isDefinitiveRefundError(e) {
  const status = e?.response?.status;
  return status != null && status >= 400 && status < 500
    && status !== 408 && status !== 429;
}

// Shown when the server's answer is genuinely unknown. Deliberately does
// NOT say "try again": the refund may already have succeeded, and the
// retained intent id is what makes a later retry safe.
const AMBIGUOUS_REFUND_MESSAGE =
  "Refund status is being verified. Do not submit another refund.";

// Oct 2026 (PR #234 correction) — a refund whose provider outcome is not
// yet known holds its items and money. The server refuses another refund
// of them (`refund_pending_provider_confirmation`); this says so first.
const PENDING_REFUND_MESSAGE =
  "Refund pending provider confirmation. Do not submit another refund.";

// Commerce Cleanup + KDS Sprint (Feb 2026) — Owner Refund modal.
// Drives `POST /api/admin/payments/{payment_id}/refund` for Stripe paid
// AND cash-completed orders. The Cash path is recorded as a manual
// refund (no gateway round-trip) so daily closing records stay
// accurate. Refund history pulls from `payment_transactions.refunds[]`
// surfaced by `GET /api/retail/orders/{order_id}/payment`.

function formatMoney(v, currency) {
  if (v == null) return "";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency || ""}`.trim();
}

function centsToDollars(c) {
  return Number(((c || 0) / 100).toFixed(2));
}

export default function RefundOrderModal({ order, onClose, onRefunded }) {
  // Sep 2026 (N-2) — one refund INTENT, one identifier, held across every
  // retry of that intent.
  //
  // Without it, a lost response or an impatient second click produced two
  // independent refunds for the same money, and nothing on either side
  // could tell them apart. The identifier is minted once per intent and
  // deliberately survives a timeout, a 5xx and a retry — those are exactly
  // the cases where the first request may already have moved money. It is
  // rotated ONLY when the server gave a definitive answer (a refund that
  // completed, or one it definitively refused) or when the operator starts
  // a genuinely different refund.
  const intentIdRef = useRef(null);
  const refundIntentId = useCallback(() => {
    if (!intentIdRef.current) intentIdRef.current = newRefundIntentId();
    return intentIdRef.current;
  }, []);
  // Minted BEFORE the request is built, never inside the try-block that
  // wraps it. `newRefundIntentId` throws when the browser has no CSPRNG,
  // and a throw from inside that block carries no HTTP status — which
  // the ambiguity classifier reads as "outcome unknown" and reports as
  // "your refund is being verified", for a request that never left the
  // browser. Returning null here lets the caller say what actually
  // happened and stop.
  const beginRefundIntent = useCallback(() => {
    try {
      return refundIntentId();
    } catch (e) {
      toast.error(
        "This browser cannot generate a secure refund reference. " +
        "Please use an up-to-date browser to issue refunds."
      );
      return null;
    }
  }, [refundIntentId]);
  const rotateRefundIntent = useCallback(() => {
    intentIdRef.current = null;
  }, []);
  // An ambiguous outcome must NOT rotate the id, and must not tell the
  // operator to try again — the refund may well have gone through.
  const [ambiguous, setAmbiguous] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payment, setPayment] = useState(null);
  const [orderDoc, setOrderDoc] = useState(order);
  const [submitting, setSubmitting] = useState(false);
  const [amountInput, setAmountInput] = useState("");
  const [reason, setReason] = useState("");
  // Sprint A (Feb 2026) — three new pieces of local state:
  //  * `success` flips true after the gateway accepts the refund. Once
  //    set the Cancel button is replaced with a plain "Close" button
  //    (there is no user-driven cancel path after Stripe accepts).
  //  * `amountError` renders under the amount input when the entered
  //    amount exceeds the remaining refundable amount — matches the
  //    backend 400 message so the operator sees the same wording
  //    whether we caught it client-side or the server did.
  //  * `completedAck` gates Submit for Completed orders — the operator
  //    must tick a warning acknowledgement so a fulfilled order is
  //    never refunded by accident.
  const [success, setSuccess] = useState(false);
  const [amountError, setAmountError] = useState(null);
  const [completedAck, setCompletedAck] = useState(false);
  // Sprint B (Feb 2026) — item-level selection state.
  //   `selectedQty[item_id]` — the quantity the operator is refunding
  //     for that line in this refund submission. 0 means "not selected".
  //   `includeTip` / `includeDelivery` — refund the tip / delivery fee
  //     along with the item lines. Disabled once those components have
  //     already been refunded on a prior submission.
  const [selectedQty, setSelectedQty] = useState({});
  const [includeTip, setIncludeTip] = useState(false);
  const [includeDelivery, setIncludeDelivery] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/retail/orders/${order.id}/payment`);
      setOrderDoc(data?.order || order);
      setPayment(data?.payment || null);
    } catch (e) {
      setError(
        e?.response?.status === 404
          ? "Order not found"
          : "Could not load payment details for this order."
      );
    } finally {
      setLoading(false);
    }
  }, [order]);

  useEffect(() => { load(); }, [load]);

  const refunds = useMemo(() => payment?.refunds || [], [payment]);
  // Oct 2026 (PR #234 correction) — a refund the provider has CONFIRMED
  // and a refund still awaiting the provider are different facts, and
  // are never added together as though both were final:
  //   * confirmed: a refund row the provider settled;
  //   * pending:   a refund row the provider accepted but has not settled,
  //                plus item refunds whose provider outcome is not yet
  //                known (the order's `refund_pending`);
  //   * a refund the provider refused returned nothing and counts as
  //     neither.
  // Both confirmed and pending money are unavailable for another refund.
  const alreadyRefundedCents = useMemo(
    () => refunds
      .filter((r) => ["succeeded", ""].includes(String(r.status || "").toLowerCase()))
      .reduce((n, r) => n + (Number(r.amount_cents) || 0), 0),
    [refunds],
  );
  const pendingRefundCents = useMemo(
    () => refunds
      .filter((r) => !["succeeded", "", "failed", "canceled", "cancelled"].includes(String(r.status || "").toLowerCase()))
      .reduce((n, r) => n + (Number(r.amount_cents) || 0), 0)
      + Number(orderDoc?.refund_pending?.pending_cents || 0),
    [refunds, orderDoc],
  );
  const hasPendingRefund = Boolean(orderDoc?.refund_pending?.has_pending) || pendingRefundCents > 0;
  const totalCents = Number(payment?.amount_cents || 0);
  const remainingCents = Math.max(0, totalCents - alreadyRefundedCents - pendingRefundCents);
  const remainingDollars = centsToDollars(remainingCents);
  const currency = (payment?.currency || orderDoc?.currency || "USD").toUpperCase();

  // Sprint B (Feb 2026) — item selector. Product tax treatment (Oct
  // 2026): the amounts are NOT computed here. Each line is refunded from
  // what it was actually billed (its recorded merchandise after
  // discounts, plus the tax recorded on it — none on an exempt line), and
  // only the server knows that; the modal shows the server's preview.
  const orderItems = useMemo(() => orderDoc?.items || [], [orderDoc]);
  const taxableByLine = useMemo(() => lineTaxableMap(orderDoc), [orderDoc]);
  const orderTip = Number(orderDoc?.tip || 0);
  const orderDelivery = Number(orderDoc?.delivery || 0);
  const tipAlreadyRefundedCents = useMemo(
    () => refunds.reduce((n, r) => n + (Number(r.tip_cents) || 0), 0),
    [refunds],
  );
  const deliveryAlreadyRefundedCents = useMemo(
    () => refunds.reduce((n, r) => n + (Number(r.delivery_cents) || 0), 0),
    [refunds],
  );
  const tipAlreadyRefunded = tipAlreadyRefundedCents > 0;
  const deliveryAlreadyRefunded = deliveryAlreadyRefundedCents > 0;

  // Per-line refundable quantity (`qty ordered − qty already refunded`)
  // and live selection totals.
  // Sprint B follow-up (Feb 2026) — root-cause: production orders that
  // pre-dated the cart-stamped `item_id` were slipping into the
  // amount-only fallback because we filtered them out here. Backend
  // now backfills a stable `item_id` on read; if for any reason a line
  // still arrives without one we synthesise a positional identifier
  // client-side (`__legacy-{index}`) so the operator at least SEES the
  // item panel — the submit path guards against these synthetics so
  // the amount-only fallback is used for the network call instead of
  // sending an unknown id.
  const linesView = useMemo(() => {
    return orderItems.map((it, idx) => {
      const stableId = it.item_id || `__legacy-${idx}`;
      const isSynthetic = !it.item_id;
      const ordered = Number(it.quantity || 0);
      const refundedQty = Number(it.qty_refunded || 0);
      // Units held by a refund still awaiting the provider are not
      // refundable again until the provider answers.
      const pendingQty = Number(it.qty_refund_pending || 0);
      const refundableQty = Math.max(0, ordered - refundedQty - pendingQty);
      const sel = Math.max(0, Math.min(refundableQty, Number(selectedQty[stableId] || 0)));
      const unitPrice = Number(it.unit_price || 0);
      const subtotal = unitPrice * sel;
      return { ...it, item_id: stableId, isSynthetic, ordered, refundedQty, pendingQty, refundableQty, selectedQty: sel, subtotal };
    });
  }, [orderItems, selectedQty]);
  // Item-level submit path requires every selectable line to have a
  // real (non-synthetic) item_id. If ANY selected line is synthetic
  // the modal will refuse to send an item-level payload.
  const hasSyntheticLines = linesView.some((l) => l.isSynthetic);
  // Product tax treatment (Oct 2026) — an order carrying a manual Order
  // Discount is refunded by item again: the server now refunds each line
  // at its recorded post-discount amount, which is what had made item
  // refunds unsafe for such orders.
  const amountOnly = hasSyntheticLines;
  const hasItemSelection = linesView.some((l) => l.selectedQty > 0);
  const usingItemPath = hasItemSelection && !amountOnly;

  // The server's costing of the current selection
  // (`POST /admin/payments/{id}/refund/preview` — the refund's own code,
  // no money moves). Stale answers are discarded by sequence number.
  const [preview, setPreview] = useState({ status: "idle", data: null });
  const previewSeq = useRef(0);
  const selectionItems = useMemo(
    () => linesView.filter((l) => l.selectedQty > 0).map((l) => ({ item_id: l.item_id, qty: l.selectedQty })),
    [linesView],
  );
  const selectionKey = JSON.stringify([selectionItems, includeTip && !tipAlreadyRefunded,
    includeDelivery && !deliveryAlreadyRefunded]);
  useEffect(() => {
    if (!usingItemPath || !payment?.id) {
      previewSeq.current += 1;
      setPreview({ status: "idle", data: null });
      return;
    }
    const seq = ++previewSeq.current;
    setPreview({ status: "loading", data: null });
    api.post(`/admin/payments/${payment.id}/refund/preview`, {
      items: selectionItems,
      include_tip: includeTip && !tipAlreadyRefunded,
      include_delivery: includeDelivery && !deliveryAlreadyRefunded,
    }).then(({ data }) => {
      if (seq === previewSeq.current) setPreview({ status: "ready", data });
    }).catch((e) => {
      if (seq !== previewSeq.current) return;
      const det = e?.response?.data?.detail;
      const message = (det && typeof det === "object" && det.message) || (typeof det === "string" && det) || null;
      setPreview({ status: "error", data: null, message });
    });
  }, [selectionKey, usingItemPath, payment?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const previewReady = preview.status === "ready" && preview.data;
  const computedTotal = previewReady ? centsToDollars(preview.data.total_cents) : null;

  // "Refund all remaining" — one-click fills every line with its
  // refundable qty and ticks tip / delivery if there's an unrefunded
  // balance for either.
  const applyRefundAllRemaining = () => {
    const next = {};
    linesView.forEach((l) => {
      if (l.refundableQty > 0) next[l.item_id] = l.refundableQty;
    });
    setSelectedQty(next);
    if (orderTip > 0 && !tipAlreadyRefunded) setIncludeTip(true);
    if (orderDelivery > 0 && !deliveryAlreadyRefunded) setIncludeDelivery(true);
  };

  // Default the amount-only fallback input to the remaining balance
  // (only used when the operator does NOT select any item quantities).
  useEffect(() => {
    if (payment && amountInput === "" && remainingDollars > 0) {
      setAmountInput(String(remainingDollars.toFixed(2)));
    }
  }, [payment, remainingDollars]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCashMethod = (payment?.method || "").toLowerCase() === "cash";
  const isRefundable = payment && remainingCents > 0
    && ["paid", "partial_refund"].includes((payment.status || "").toLowerCase());
  // Sprint A — Completed fulfillment orders require an explicit
  // acknowledgement before Submit is enabled. Refunding a completed
  // order does NOT reverse the fulfillment; the acknowledgement forces
  // the operator to confirm they understand that.
  const orderStatus = (orderDoc?.status || "").toLowerCase();
  const requiresCompletedAck = orderStatus === "completed";

  // Reset the amount-error banner whenever the user edits the selection.
  useEffect(() => {
    if (amountError) setAmountError(null);
  }, [amountInput, selectedQty, includeTip, includeDelivery]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!payment?.id) {
      toast.error("No payment is recorded against this order. Refund cannot be processed.");
      return;
    }
    if (!isRefundable) {
      toast.error("This payment cannot be refunded any further.");
      return;
    }
    // Sprint B (Feb 2026) — item-level path is the primary flow. When
    // the operator selects at least one item, send `items[]` +
    // `include_tip` + `include_delivery` and let the backend
    // authoritatively compute the amount (with proportional tax).
    // Amount-only fallback is retained for the cash-manual refund
    // flow (e.g., staff owes customer $2 with no item context).
    // Sprint B follow-up (Feb 2026) — never send synthetic item_ids
    // (`__legacy-*`) to the backend; those lines have no stable
    // identifier the engine can validate against. Fall through to the
    // amount-only path instead.
    if (usingItemPath) {
      if (!previewReady) {
        setAmountError("The refund amount has not been calculated yet.");
        return;
      }
      if (computedTotal <= 0) {
        setAmountError("Select at least one item quantity to refund.");
        return;
      }
      const roundedCents = Math.round(computedTotal * 100);
      if (roundedCents > remainingCents) {
        setAmountError(
          `Refund amount cannot exceed the remaining refundable amount of ${formatMoney(remainingDollars, currency)}.`
        );
        return;
      }
      if (!reason.trim()) {
        toast.error("Please add a reason — it's saved on the refund record.");
        return;
      }
      if (requiresCompletedAck && !completedAck) {
        toast.error("Please acknowledge the Completed-order warning before refunding.");
        return;
      }
      const itemIntentId = beginRefundIntent();
      if (!itemIntentId) return;
      setSubmitting(true);
      try {
        const itemsPayload = linesView
          .filter((l) => l.selectedQty > 0)
          .map((l) => ({ item_id: l.item_id, qty: l.selectedQty }));
        await api.post(`/admin/payments/${payment.id}/refund`, {
          items: itemsPayload,
          include_tip: includeTip && !tipAlreadyRefunded,
          include_delivery: includeDelivery && !deliveryAlreadyRefunded,
          reason: reason.trim().slice(0, 280),
          idempotency_key: itemIntentId,
          // Sep 2026 (third independent review of PR #185), invariant
          // I-7 — name the payment this refund is FOR.
          //
          // This modal always targets the payment that SETTLED the order.
          // When the order also carries an unrefunded duplicate charge,
          // the server refuses a refund of the settling payment unless
          // the caller says so explicitly, because the two controls look
          // alike and the wrong click returns the customer's legitimate
          // payment while leaving the double charge in place. This field
          // is that statement of intent. A duplicate is refunded from
          // the order details drawer instead, by its own payment id.
          target_payment_id: payment.id,
        });
        toast.success(
          isCashMethod
            ? `Manual cash refund of ${formatMoney(computedTotal, currency)} recorded.`
            : `Refund of ${formatMoney(computedTotal, currency)} issued.`,
        );
        // Definitive success — the next refund is a genuinely new intent.
        rotateRefundIntent();
        await load();
        setSelectedQty({});
        setIncludeTip(false);
        setIncludeDelivery(false);
        setReason("");
        setSuccess(true);
        onRefunded && onRefunded();
      } catch (e) {
        const det = e?.response?.data?.detail;
        if (isAmbiguousRefundError(e)) {
          // Ambiguous: the request may have moved money. Keep the intent
          // id (a retry of THIS intent is safe; a new one is not) and say
          // so plainly instead of inviting a second refund.
          setAmbiguous(true);
          toast.error(AMBIGUOUS_REFUND_MESSAGE);
        } else if (det && typeof det === "object" && det.code === "refund_pending_provider_confirmation") {
          // Refused before any money moved for THIS request: what it asked
          // for is held by a refund still awaiting the provider. Show the
          // pending state rather than inviting a retry.
          rotateRefundIntent();
          setAmountError(det.message || PENDING_REFUND_MESSAGE);
          toast.error(PENDING_REFUND_MESSAGE);
          await load();
        } else if (det && typeof det === "object" && (det.code === "refund_exceeds_remaining" || det.code === "item_qty_exceeds_refundable")) {
          // Definitively refused before any money moved.
          rotateRefundIntent();
          setAmountError(det.message || "Refund exceeds remaining refundable amount.");
        } else if (det && typeof det === "object" && det.code === "refund_intent_conflict") {
          rotateRefundIntent();
          toast.error(det.message || "Start a new refund to make a different one.");
        } else {
          if (isDefinitiveRefundError(e)) rotateRefundIntent();
          const msg = (det && typeof det === "object" && det.message)
            || (typeof det === "string" && det)
            || "Refund failed. Please try again.";
          toast.error(msg);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ---------- Amount-only fallback (cash flat refund) ----------
    const amt = parseFloat(amountInput);
    if (!Number.isFinite(amt) || amt <= 0) {
      setAmountError("Enter a valid refund amount.");
      return;
    }
    const cents = Math.round(amt * 100);
    if (cents > remainingCents) {
      setAmountError(
        `Refund amount cannot exceed the remaining refundable amount of ${formatMoney(remainingDollars, currency)}.`
      );
      return;
    }
    if (!reason.trim()) {
      toast.error("Please add a reason — it's saved on the refund record.");
      return;
    }
    if (requiresCompletedAck && !completedAck) {
      toast.error("Please acknowledge the Completed-order warning before refunding.");
      return;
    }
    const amountIntentId = beginRefundIntent();
    if (!amountIntentId) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/payments/${payment.id}/refund`, {
        amount_cents: cents,
        reason: reason.trim().slice(0, 280),
        idempotency_key: amountIntentId,
        // See the item-refund call above — same invariant, same field.
        target_payment_id: payment.id,
      });
      toast.success(
        isCashMethod
          ? `Manual cash refund of ${formatMoney(amt, currency)} recorded.`
          : `Refund of ${formatMoney(amt, currency)} issued.`,
      );
      rotateRefundIntent();
      await load();
      setAmountInput("");
      setReason("");
      setSuccess(true);
      onRefunded && onRefunded();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (isAmbiguousRefundError(e)) {
        setAmbiguous(true);
        toast.error(AMBIGUOUS_REFUND_MESSAGE);
      } else if (det && typeof det === "object" && det.code === "refund_exceeds_remaining") {
        rotateRefundIntent();
        setAmountError(det.message || "Refund exceeds remaining refundable amount.");
      } else if (det && typeof det === "object" && det.code === "refund_intent_conflict") {
        rotateRefundIntent();
        toast.error(det.message || "Start a new refund to make a different one.");
      } else {
        if (isDefinitiveRefundError(e)) rotateRefundIntent();
        const msg = (det && typeof det === "object" && det.message)
          || (typeof det === "string" && det)
          || "Refund failed. Please try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto"
      data-testid="refund-modal"
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Refund</div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="refund-order-code">
              {orderDoc?.order_code || order?.order_code}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="refund-close"
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-12 grid place-items-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 flex items-center gap-2" data-testid="refund-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        ) : !payment ? (
          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2" data-testid="refund-no-payment">
            No payment record is attached to this order yet. Refunds are only
            available once payment has been captured.
          </div>
        ) : (
          <>
            <PaymentSummary
              payment={payment}
              currency={currency}
              totalCents={totalCents}
              alreadyRefundedCents={alreadyRefundedCents}
              pendingRefundCents={pendingRefundCents}
              remainingCents={remainingCents}
            />

            {hasPendingRefund && (
              <div
                className="mt-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs px-3 py-2 flex items-start gap-2"
                data-testid="refund-pending-banner"
                role="status"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>{PENDING_REFUND_MESSAGE}</strong> The items and amount it covers
                  stay reserved for it until the payment provider confirms the
                  result, and cannot be refunded again in the meantime.
                </span>
              </div>
            )}

            {isCashMethod && (
              <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2" data-testid="refund-cash-notice">
                <strong>Manual cash refund.</strong> No gateway is contacted —
                this entry is recorded in Rudras-AI so daily closing matches
                the drawer. Hand the cash back to the customer at the counter.
              </div>
            )}

            {requiresCompletedAck && !success && (
              <div
                className="mt-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2"
                data-testid="refund-completed-warning"
              >
                <strong>This order is Completed.</strong> Issuing a refund will
                NOT change the order status back — the fulfillment is complete,
                only the payment is being reversed. Operator name, reason,
                amount, and timestamp are recorded on the refund history.
              </div>
            )}

            <div className="mt-4 space-y-3">
              {/* Sprint B follow-up (Feb 2026) — visible diagnostic
                  banner so operators (and support) can see WHICH refund
                  mode the modal picked and why. Silent fallbacks were
                  what hid the amount-only-vs-item-level regression on
                  production. */}
              {linesView.length > 0 ? (
                <div
                  className={`rounded-md text-[11px] px-2.5 py-1.5 border ${
                    hasSyntheticLines
                      ? "bg-amber-50 border-amber-200 text-amber-800"
                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                  data-testid="refund-mode-banner"
                >
                  <strong>{hasSyntheticLines ? "Legacy amount-only" : "Item-level"}</strong> refund mode
                  {hasSyntheticLines && (
                    <span className="ml-1">
                      — this order has {linesView.filter((l) => l.isSynthetic).length} line(s) without a stable identifier; use the amount field below.
                    </span>
                  )}
                  <span className="ml-1 opacity-70">
                    ({linesView.length} item{linesView.length === 1 ? "" : "s"} loaded)
                  </span>
                </div>
              ) : (
                <div
                  className="rounded-md text-[11px] px-2.5 py-1.5 border bg-slate-100 border-slate-200 text-slate-700"
                  data-testid="refund-mode-banner"
                >
                  <strong>Legacy amount-only</strong> refund mode — no line items loaded for this order.
                </div>
              )}

              {/* Sprint B (Feb 2026) — item selector. Primary refund
                  path. Amount-only fallback below only shows when the
                  operator elects NOT to select items (e.g., recording
                  a manual cash flat-refund with no per-line context).
              */}
              {linesView.length > 0 && !amountOnly && (
                <div data-testid="refund-items-panel">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-slate-600">Refund items</label>
                    <button
                      type="button"
                      onClick={applyRefundAllRemaining}
                      disabled={!isRefundable || submitting || success}
                      data-testid="refund-all-remaining"
                      className="text-[11px] px-2 py-1 rounded-md border border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                    >
                      Refund all remaining
                    </button>
                  </div>
                  <ul className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100">
                    {linesView.map((l) => {
                      const disabled = !isRefundable || submitting || success || l.refundableQty === 0;
                      return (
                        <li
                          key={l.item_id}
                          className="px-3 py-2 flex items-center justify-between gap-3"
                          data-testid={`refund-item-row-${l.item_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">{taxMarkedName(l.product_name, taxableByLine[l.item_id])}</div>
                            <div className="text-[11px] text-slate-500">
                              Ordered {l.ordered} · Refunded {l.refundedQty}
                              {l.pendingQty > 0 && (
                                <span className="text-amber-700" data-testid={`refund-item-pending-${l.item_id}`}> · Pending {l.pendingQty}</span>
                              )}
                              {" "}· Refundable {l.refundableQty} · {formatMoney(l.unit_price, currency)} ea
                            </div>
                          </div>
                          <input
                            type="number"
                            min="0"
                            max={l.refundableQty}
                            step="1"
                            value={l.selectedQty || ""}
                            disabled={disabled}
                            onChange={(e) => {
                              const raw = parseInt(e.target.value, 10);
                              const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(l.refundableQty, raw)) : 0;
                              setSelectedQty((prev) => ({ ...prev, [l.item_id]: clamped }));
                            }}
                            data-testid={`refund-item-qty-${l.item_id}`}
                            placeholder="0"
                            className="w-16 h-9 rounded-md border border-slate-300 px-2 text-sm text-center disabled:bg-slate-50 disabled:text-slate-400"
                          />
                        </li>
                      );
                    })}
                  </ul>

                  {/* Tip + delivery toggles when applicable */}
                  <div className="mt-2 space-y-1">
                    {orderTip > 0 && (
                      <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                        <input
                          type="checkbox"
                          checked={includeTip && !tipAlreadyRefunded}
                          disabled={tipAlreadyRefunded || submitting || success || !isRefundable}
                          onChange={(e) => setIncludeTip(e.target.checked)}
                          data-testid="refund-include-tip"
                          className="h-4 w-4 accent-orange-500 cursor-pointer"
                        />
                        <span>
                          Include tip ({formatMoney(orderTip, currency)})
                          {tipAlreadyRefunded && <span className="ml-1 text-slate-400">— already refunded</span>}
                        </span>
                      </label>
                    )}
                    {orderDelivery > 0 && (
                      <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                        <input
                          type="checkbox"
                          checked={includeDelivery && !deliveryAlreadyRefunded}
                          disabled={deliveryAlreadyRefunded || submitting || success || !isRefundable}
                          onChange={(e) => setIncludeDelivery(e.target.checked)}
                          data-testid="refund-include-delivery"
                          className="h-4 w-4 accent-orange-500 cursor-pointer"
                        />
                        <span>
                          Include delivery ({formatMoney(orderDelivery, currency)})
                          {deliveryAlreadyRefunded && <span className="ml-1 text-slate-400">— already refunded</span>}
                        </span>
                      </label>
                    )}
                  </div>

                  {/* The server's refund preview for this selection. With
                      no answer (still calculating, or it failed) NO amount
                      is shown and Submit stays disabled — never a figure
                      the browser invented. */}
                  {hasItemSelection && (
                    <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 space-y-0.5">
                      {preview.status === "loading" && (
                        <div className="text-xs text-slate-500" data-testid="refund-preview-loading">Calculating refund…</div>
                      )}
                      {preview.status === "error" && (
                        <div className="text-xs text-rose-700" data-testid="refund-preview-error">
                          {preview.message || "The refund amount could not be calculated. Close and reopen this order to try again."}
                        </div>
                      )}
                      {previewReady && (
                        <div data-testid="refund-preview-summary" className="space-y-0.5">
                          {(preview.data.items || []).map((row) => (
                            <div key={row.item_id} className="text-[11px] text-slate-500 flex justify-between gap-2" data-testid={`refund-preview-line-${row.item_id}`}>
                              <span className="truncate">
                                {row.qty}× {taxMarkedName(row.product_name || "", taxableByLine[row.item_id])}
                              </span>
                              <span className="whitespace-nowrap">
                                {formatMoney(centsToDollars(row.merchandise_cents ?? row.subtotal_cents), currency)} + tax {formatMoney(centsToDollars(row.tax_cents), currency)}
                              </span>
                            </div>
                          ))}
                          <RowLine
                            label="Merchandise (after discounts)"
                            value={<span data-testid="refund-preview-merchandise">{formatMoney(centsToDollars(preview.data.items_subtotal_cents), currency)}</span>}
                          />
                          <RowLine
                            label="Tax as charged"
                            value={<span data-testid="refund-preview-tax">{formatMoney(centsToDollars(preview.data.tax_cents), currency)}</span>}
                          />
                          {preview.data.tip_cents > 0 && <RowLine label="Tip" value={formatMoney(centsToDollars(preview.data.tip_cents), currency)} />}
                          {preview.data.delivery_cents > 0 && <RowLine label="Delivery" value={formatMoney(centsToDollars(preview.data.delivery_cents), currency)} />}
                          <div className="flex items-center justify-between text-sm pt-1 border-t border-slate-200">
                            <span className="font-semibold">Refund total</span>
                            <span className="font-bold" data-testid="refund-computed-total">
                              {formatMoney(computedTotal, currency)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Amount-only fallback — hidden once the operator has
                  selected item quantities. Retained for cash-manual
                  refunds with no per-line context AND for legacy
                  orders where every line is synthetic (no stable
                  item_id). */}
              {(amountOnly || !hasItemSelection) && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Amount ({currency})</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amountInput}
                      disabled={!isRefundable || submitting || success}
                      onChange={(e) => setAmountInput(e.target.value)}
                      data-testid="refund-amount"
                      className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                      placeholder="0.00"
                    />
                    <button
                      type="button"
                      disabled={!isRefundable || submitting || success}
                      /* Gate C5A-6 (A) — "Full" now ALLOCATES as well as
                         filling the amount, whenever this order has real
                         line items to allocate against. Before, it only
                         typed the remaining balance into the amount box,
                         so the operator's full refund went down the
                         amount-only path and left every line reading
                         "refunded 0, refundable 4" beside a payment that
                         said Fully Refunded. Orders with no usable line
                         ids (synthetic `__legacy-*`) keep the plain
                         amount behaviour — there is nothing to allocate
                         to, and inventing an allocation would be worse
                         than leaving it explicit. */
                      onClick={() => {
                        setAmountInput(remainingDollars.toFixed(2));
                        if (!amountOnly && linesView.some((l) => l.refundableQty > 0)) {
                          applyRefundAllRemaining();
                        }
                      }}
                      data-testid="refund-amount-full"
                      className="text-xs px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Full ({formatMoney(remainingDollars, currency)})
                    </button>
                  </div>
                  {amountError ? (
                    <div className="text-[11px] text-red-600 mt-1" data-testid="refund-amount-error">
                      {amountError}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Max refundable: {formatMoney(remainingDollars, currency)}
                    </div>
                  )}
                </div>
              )}
              {hasItemSelection && amountError && (
                <div className="text-[11px] text-red-600" data-testid="refund-amount-error">
                  {amountError}
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-600">Reason (required)</label>
                <textarea
                  value={reason}
                  disabled={!isRefundable || submitting || success}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="refund-reason"
                  className="mt-1 w-full min-h-[64px] rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  placeholder="e.g. Customer changed mind, kitchen mistake, allergen…"
                  maxLength={280}
                />
              </div>

              {requiresCompletedAck && !success && (
                <label className="flex items-start gap-2 text-xs text-slate-700 select-none">
                  <input
                    type="checkbox"
                    checked={completedAck}
                    onChange={(e) => setCompletedAck(e.target.checked)}
                    disabled={submitting}
                    data-testid="refund-completed-ack"
                    className="mt-0.5 h-4 w-4 accent-red-500 cursor-pointer"
                  />
                  <span>
                    I understand this order is Completed and refunding here
                    reverses the payment only.
                  </span>
                </label>
              )}

              {success ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2" data-testid="refund-success-banner">
                    <strong>Refund recorded.</strong> No further action needed.
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="refund-close-btn"
                    className="h-11 px-5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {ambiguous && (
                    <div
                      data-testid="refund-ambiguous-notice"
                      className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                    >
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        {AMBIGUOUS_REFUND_MESSAGE} Reopen this refund window to
                        see the confirmed result.
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {/* Sep 2026 (N-2) — `ambiguous` latches this button
                        off. After an unknown outcome the safe action is
                        NOT to submit again: the first request may already
                        have refunded the money. The operator reopens the
                        modal, which re-reads the payment, to see what
                        actually happened. */}
                    <button
                      type="button"
                      onClick={submit}
                      disabled={!isRefundable || submitting || ambiguous || (requiresCompletedAck && !completedAck)
                        || (usingItemPath && !previewReady)}
                      data-testid="refund-submit"
                      className="flex-1 h-11 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"
                    >
                      {submitting
                        ? "Processing…"
                        : ambiguous
                          ? "Verifying…"
                          : isCashMethod
                            ? "Record Manual Cash Refund"
                            : "Submit Refund"}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={submitting}
                      data-testid="refund-cancel"
                      className="h-11 px-4 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {refunds.length > 0 && (
              <RefundHistory refunds={refunds} currency={currency} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PaymentSummary({ payment, currency, totalCents, alreadyRefundedCents, pendingRefundCents = 0, remainingCents }) {
  const MethodIcon = (payment.method || "").toLowerCase() === "cash" ? Banknote : CreditCard;
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-sm space-y-1" data-testid="refund-payment-summary">
      <div className="flex items-center justify-between">
        <span className="text-slate-500 inline-flex items-center gap-1.5 text-xs">
          <MethodIcon className="w-3.5 h-3.5" />
          {(payment.method || "").toUpperCase()} · {payment.provider || ""}
        </span>
        <span className="font-mono text-xs text-slate-500" data-testid="refund-payment-ref">
          {payment.reference_number || payment.id?.slice(0, 8) || ""}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-slate-600">Order total</span>
        <span className="font-semibold">{formatMoney(centsToDollars(totalCents), currency)}</span>
      </div>
      {alreadyRefundedCents > 0 && (
        <div className="flex items-center justify-between text-amber-700">
          <span>Already refunded</span>
          <span className="font-semibold" data-testid="refund-already">−{formatMoney(centsToDollars(alreadyRefundedCents), currency)}</span>
        </div>
      )}
      {pendingRefundCents > 0 && (
        <div className="flex items-center justify-between text-amber-800">
          <span>Pending provider confirmation</span>
          <span className="font-semibold" data-testid="refund-pending-amount">−{formatMoney(centsToDollars(pendingRefundCents), currency)}</span>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-slate-200 pt-1.5">
        <span className="text-slate-600">Remaining refundable</span>
        <span className="font-bold text-slate-900" data-testid="refund-remaining">
          {formatMoney(centsToDollars(remainingCents), currency)}
        </span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">Status</span>
        <span className={`px-2 py-0.5 rounded-full ${payment.status === "refunded" ? "bg-red-100 text-red-700" : payment.status === "partial_refund" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
          {payment.status}
        </span>
      </div>
    </div>
  );
}

function RefundHistory({ refunds, currency }) {
  return (
    <div className="mt-4" data-testid="refund-history">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
        <RefreshCw className="w-3 h-3" /> Refund history
      </div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
        {refunds.map((r, i) => (
          <li key={r.id || i} className="px-3 py-2 text-sm flex items-start justify-between gap-3" data-testid={`refund-history-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{formatMoney(centsToDollars(r.amount_cents), currency)}</div>
              <div className="text-xs text-slate-500 truncate">{r.reason || "—"}</div>
              {/* Sprint B (Feb 2026) — item-level refund detail. Legacy
                  amount-only refunds omit these fields. */}
              {Array.isArray(r.items) && r.items.length > 0 && (
                <ul className="mt-1 text-[11px] text-slate-500">
                  {r.items.map((li, k) => (
                    <li key={k}>• {li.qty}× {li.product_name || li.item_id}</li>
                  ))}
                  {(r.tip_cents || 0) > 0 && <li>• Tip: {formatMoney(centsToDollars(r.tip_cents), currency)}</li>}
                  {(r.delivery_cents || 0) > 0 && <li>• Delivery: {formatMoney(centsToDollars(r.delivery_cents), currency)}</li>}
                </ul>
              )}
            </div>
            <div className="text-[11px] text-slate-400 whitespace-nowrap">
              {r.refunded_at ? new Date(r.refunded_at).toLocaleString() : ""}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Sprint B (Feb 2026) — small row helper used by the computed-refund
// summary. Kept local to this file since it's not reused elsewhere.
function RowLine({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-600">{label}</span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}
